"use client";

import type { Translate } from "@/lib/i18n/translate";
/**
 * HERO headline rotator — cycles every 1.9s through the tools hausverwalter.ai
 * replaces (prototype `_initRotator`). Each change replays the `mkhero-rot`
 * fade-up (via React `key`).
 *
 * Desktop: one italic-verdigris line after "Kündige".
 * Mobile: line 1 = "Kündige" + article (ink); line 2 = target (verdigris italic).
 */
import {
  type CSSProperties,
  Fragment,
  type ReactNode,
  useEffect,
  useState,
} from "react";

import { useTranslations } from "next-intl";
import { H } from "./hero-tokens";

type HeroRotatorArticle = "dein" | "deine" | "deinen";

type HeroRotatorItem = {
  readonly article: HeroRotatorArticle;
  readonly target: string;
  /** Shorter line-2 label on mobile when the full target does not fit. */
  readonly mobileTarget?: string;
};

/**
 * Verbatim rotator entries from the prototype, split for the mobile two-line layout.
 *
 * Homepage marketing uses "Vorgang" / "Vorgangssystem" everywhere — that is
 * the industry word Hausverwalter know. Product UI uses "Konversation"
 * (internal/internet term). Do not rename homepage "Vorgang" back to
 * "Konversation".
 */
function rotatorItems(t: Translate): readonly HeroRotatorItem[] {
  return [
    {
      article: "dein",
      target: t("dokumentenmanagementSystem"),
      mobileTarget: "DMS",
    },
    { article: "deine", target: t("portalloesung") },
    { article: "deine", target: t("telefonanlage") },
    { article: "dein", target: "Microsoft 365" },
    { article: "deinen", target: t("kiAnrufbeantworter") },
    { article: "dein", target: t("vorgangssystem") },
    { article: "deine", target: t("eigentuemerversammlungsSoftware") },
    { article: "deinen", target: "Server" },
  ];
}

const ROTATE_INTERVAL_MS = 1900;

function formatRotatorPhrase(item: HeroRotatorItem): string {
  return `${item.article} ${item.target}`;
}

/** Scales line-2 font so long targets stay on one row (max two headline lines on mobile). */
function mobileTargetFontSize(target: string): number {
  const len: number = target.length;
  if (len <= 15) return 38;
  if (len <= 22) return 34;
  if (len <= 28) return 30;
  return 26;
}

function useHeroRotatorIndex(): number {
  const t = useTranslations("marketing.heroRotator");
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % rotatorItems(t).length);
    }, ROTATE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, []);

  return index;
}

export function HeroRotatorPhrase({
  style,
}: {
  readonly style?: CSSProperties;
}): ReactNode {
  const t = useTranslations("marketing.heroRotator");
  const index: number = useHeroRotatorIndex();
  const item = rotatorItems(t)[index];
  if (!item) return null;

  return (
    <span
      key={index}
      className="mkhero-rotator mkhero-rotator-target"
      style={{
        fontStyle: "italic",
        color: H.accent,
        willChange: "transform, opacity",
        ...style,
      }}
    >
      {formatRotatorPhrase(item)}
    </span>
  );
}

/** Mobile H1 rotator — "Kündige {article}" on line 1, target on line 2. */
export function HeroMobileRotatorHeadline(): ReactNode {
  const t = useTranslations("marketing.heroRotator");
  const index: number = useHeroRotatorIndex();
  const item = rotatorItems(t)[index];
  if (!item) return null;
  const mobileTarget: string = item.mobileTarget ?? item.target;
  const targetFontSize: number = mobileTargetFontSize(mobileTarget);

  return (
    <Fragment key={index}>
      <span className="mkhero-rotator" style={{ display: "block" }}>
        {t("kuendige")} {item.article}
      </span>
      <span
        className="mkhero-rotator mkhero-rotator-target"
        style={{
          display: "block",
          fontStyle: "italic",
          color: H.accent,
          fontSize: targetFontSize,
          lineHeight: 1.12,
          whiteSpace: "nowrap",
          willChange: "transform, opacity",
        }}
      >
        {mobileTarget}
      </span>
    </Fragment>
  );
}
